import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Plus, Mail } from "lucide-react";
import EventDetailsCard from "@/components/admin/events/EventDetailsCard";
import TablePositionsCard from "@/components/admin/events/TablePositionsCard";
import EventStaffAssignmentCard from "@/components/admin/events/EventStaffAssignmentCard";
import ReferralInfoCard from "@/components/admin/events/ReferralInfoCard";
import EventWaitlistCard from "@/components/admin/events/EventWaitlistCard";
import ManageWaitlistPopover from "@/components/admin/events/ManageWaitlistPopover";
import { ManagePositionsDialog } from "@/components/admin/events/ManagePositionsDialog";
import { AdminEventDetailDTO, EventPositionDTO } from "./types";
import { Position } from "@/services/positionService";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { notificationService } from "@/services/notificationService";
import { BackLink } from "@/components/brand/BackLink";

interface EventWithPositions extends AdminEventDetailDTO {
  eventPositions: EventPositionDTO[];
}

export default function AdminEventDetails() {
  const { id } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventWithPositions | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isManagePositionsOpen, setIsManagePositionsOpen] = useState(false);
  const [availablePositions, setAvailablePositions] = useState<Position[]>([]);
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  // A quiet reload keeps the page in place, so what a card just showed survives.
  const fetchEventDetails = useCallback(
    async ({ quiet = false } = {}) => {
      if (!id) return;

      if (!quiet) setLoading(true);
      try {
        const response = await axiosInstance.get<EventWithPositions>(
          `${process.env.REACT_APP_BASE_API_URL}event/${id}`,
        );
        setEvent(response.data);
      } catch (error) {
        console.error("Error fetching event details:", error);
        setError("Failed to load event details. Please try again later.");
        toast({
          title: "Error",
          description: "Failed to load event details",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [id, toast],
  );

  const fetchAvailablePositions = useCallback(async () => {
    try {
      const response = await axiosInstance.get<Position[]>(
        `${process.env.REACT_APP_BASE_API_URL}position`,
      );
      setAvailablePositions(response.data);
    } catch (error) {
      console.error("Error fetching positions:", error);
      toast({
        title: "Error",
        description: "Failed to load available positions",
        variant: "destructive",
      });
    }
  }, [toast]);

  useEffect(() => {
    fetchEventDetails();
    fetchAvailablePositions();
  }, [fetchEventDetails, fetchAvailablePositions]);

  const handleStaffUpdate = () => {
    fetchEventDetails();
  };

  const handleCancelEvent = async () => {
    if (!id) return;
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_BASE_API_URL}event/${id}/cancel`,
      );
      toast({
        title: "Event Cancelled",
        description: "The event has been cancelled successfully.",
        variant: "destructive",
      });
      navigate("/admin/events");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel the event.",
        variant: "destructive",
      });
    }
  };

  const handleSendEventOpeningNotification = async () => {
    if (!id) return;

    setIsSendingNotification(true);
    try {
      await notificationService.sendEventOpeningNotification(id);
      toast({
        title: "Notification Sent",
        description:
          "Event opening notification has been sent to all active employees.",
        variant: "success",
      });
    } catch (error) {
      console.error("Error sending event opening notification:", error);
      toast({
        title: "Error",
        description:
          "Failed to send event opening notification. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingNotification(false);
    }
  };

  const handleWaitlistCreated = () => {
    fetchEventDetails();
  };

  if (loading) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="mt-4 text-lg">Loading event details...</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-lg mx-auto text-center space-y-4">
          <h1 className="text-2xl font-bold text-destructive">
            Error Loading Event
          </h1>
          <p className="text-muted-foreground">{error || "Event not found"}</p>
          <Button onClick={() => navigate("/admin/events")}>
            Return to Events
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <BackLink to="/admin/events">Back to Events</BackLink>
      </div>

      <div className="space-y-6 max-w-6xl mx-auto">
        <EventDetailsCard
          event={event}
          onCancelEvent={handleCancelEvent}
          onInvoicesChanged={() => fetchEventDetails({ quiet: true })}
        />

        {event.referralCode && event.referralCode.trim() !== "" && (
          <ReferralInfoCard referralCode={event.referralCode} />
        )}

        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <h2 className="font-display text-2xl font-bold">Event Positions</h2>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleSendEventOpeningNotification}
              disabled={isSendingNotification}
              variant="outline"
              className="gap-2"
            >
              {isSendingNotification ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
              Notify Employees of Openings
            </Button>
            <ManageWaitlistPopover
              eventId={id as string}
              onWaitlistCreated={handleWaitlistCreated}
              hasWaitlist={event.hasWaitlist}
              currentMaxWaitlistSize={event.maxWaitlistSize}
            />
            <Button
              onClick={() => setIsManagePositionsOpen(true)}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Manage Positions
            </Button>
          </div>
        </div>

        <TablePositionsCard eventPositions={event.eventPositions} />
        <EventStaffAssignmentCard
          eventId={id as string}
          eventPositions={event.eventPositions}
          onStaffUpdate={handleStaffUpdate}
        />
      </div>

      <ManagePositionsDialog
        isOpen={isManagePositionsOpen}
        onOpenChange={setIsManagePositionsOpen}
        eventId={id as string}
        currentPositions={event.eventPositions}
        availablePositions={availablePositions}
        onPositionsUpdated={fetchEventDetails}
      />

      <div className="max-w-6xl mx-auto py-6">
        <EventWaitlistCard
          hasWaitlist={event.hasWaitlist}
          currentWaitlistSize={event.currentWaitlistSize}
          maxWaitlistSize={event.maxWaitlistSize}
          waitlistedUsers={event.waitlistedUsers}
          eventId={id as string}
          onUserRemoved={fetchEventDetails}
        />
      </div>
    </div>
  );
}
